import { createContext, useContext, type ReactNode } from 'react';
import { adminApi } from './api.js';
import { useAsync } from './useAsync.js';

/**
 * What this session may do, answered by the server.
 *
 * ── THE PORTAL HOLDS NO PERMISSION RULES, AND MUST NOT GROW ANY ──────
 *
 * Every action here used to render for everybody and let the server refuse it.
 * That is the right security posture — auth is the only authority and still is —
 * and it produced a bad experience: a Support Admin could open the New agreement
 * form, fill it in, press Save and only then learn that `agreement:write` is
 * Legal's alone. Jon hit exactly that signed in as a Support Admin.
 *
 * The obvious fix is a table of role-to-button rules in this repo, and it is the
 * wrong one. That table is a SECOND COPY of `lib/access.ts` in a codebase that
 * cannot see it, deployed separately, and it goes quietly wrong the day a role's
 * grant changes. This portal has already paid for exactly that: the Users table
 * tinted `admin` amber until auth#270 renamed the role, after which the
 * highlight matched nothing and nobody noticed, because a rule that silently
 * stops matching looks identical to one with nothing to match.
 *
 * So the server answers instead. `GET /admin/capabilities` returns the pairs
 * this session holds, derived from the same statement `requirePermission`
 * enforces with and through the same `can()`. What lives here is a set of
 * strings and a comparison. There is no rule to drift.
 *
 * ── AN AFFORDANCE, NOT A CONTROL ─────────────────────────────────────
 *
 * Nothing here protects anything. The value arrives over the wire and could be
 * edited in a console; every endpoint still runs its own check, unchanged. What
 * this buys is that a person finds out before typing rather than after saving.
 *
 * ── AND IT IS ONE-DIRECTIONAL ────────────────────────────────────────
 *
 * Absent means a definite refusal, and may be shown as one. Present means only
 * that the ROLE is not what would refuse — auth's admin-protection rules also
 * refuse by TARGET, so a Support Admin holds `user:ban` and still cannot ban
 * another member of staff. That check depends on the row, not the session, and
 * no session-wide answer can carry it.
 *
 * The practical rule for a caller: use this to disable, never to promise. A
 * control that is enabled must still handle its refusal.
 */

/** `resource:action`, exactly as auth's statement spells it. Not an enum here:
 *  the authority is auth's `STATEMENT`, and a union duplicated in this repo
 *  would be the copy this whole module exists to avoid. */
export type Capability = string;

/**
 * `undefined` until the answer arrives, and that distinction is load-bearing.
 *
 * There are three states, not two: holds it, does not hold it, and do not know
 * yet. Collapsing the third into "does not hold it" disables every control on the
 * screen for the moment after navigation, which is worse than the problem being
 * solved — a Global Admin would watch the portal disable itself and come back.
 * Collapsing it into "holds it" is what `useCan` does instead, deliberately.
 */
const CapabilityContext = createContext<Set<Capability> | undefined>(undefined);

/**
 * Whether the answer is still on its way, as distinct from having failed.
 *
 * `undefined` above means both, and for a button that is right: optimistic
 * either way. It is wrong for anything that ACTS on arrival (org-admin#134).
 * Opening `#/ci-fleet` starts a sign-in redirect to fleet-api, and doing that
 * in the moment before the answer lands sent a Support Admin to a token auth
 * then refused. Such a caller waits for this to clear, and a failed fetch
 * still clears it, so the fallback stays "behave as before".
 */
const CapabilitiesLoading = createContext<boolean>(false);

/**
 * Fetches once, for the life of the shell.
 *
 * Capabilities change when somebody's roles change, and a session whose roles
 * changed underneath it has a bigger problem than a stale button — the server
 * has already started refusing or allowing differently, and a reload is the
 * honest fix. Re-fetching per screen would spend a request per navigation to
 * narrow a window that a refresh closes anyway.
 *
 * A FAILED fetch leaves the value undefined rather than empty, so the portal
 * falls back to today's behaviour — every control live, the server refusing
 * what it refuses. That is the right direction to fail: the alternative is a
 * portal that disables everything because one request failed, which looks exactly
 * like a permissions problem and would send people to ask for access they
 * already have.
 *
 * That branch is not hypothetical, and it is what makes the DEPLOY ORDER free.
 * A portal shipped ahead of its auth meets a bare 404 on this route — the same
 * 404 a refusal gives, indistinguishable by design — and quietly behaves as it
 * did before this module existed. So the two repos can go out in either order,
 * and the endpoint's absence costs the affordance rather than the portal.
 */
export function CapabilityProvider({ children }: { children: ReactNode }) {
  const { state } = useAsync(() => adminApi.capabilities(), []);
  const value = state.status === 'ready' ? new Set(state.data.capabilities) : undefined;

  return (
    <CapabilitiesLoading.Provider value={state.status === 'loading'}>
      <CapabilityContext.Provider value={value}>{children}</CapabilityContext.Provider>
    </CapabilitiesLoading.Provider>
  );
}

/**
 * May this session do these things?
 *
 * OPTIMISTIC WHILE UNKNOWN, per the note on the context: before the answer
 * arrives, and if it never does, this says yes and the screen behaves as it did
 * before any of this existed.
 *
 * Every capability named must be held, matching `can()` on the server, which
 * needs one role to hold every action in a request rather than satisfying each
 * from a different role.
 */
export function useCan(...capabilities: Capability[]): boolean {
  return holds(useContext(CapabilityContext), capabilities);
}

/**
 * The decision itself, without React.
 *
 * Split out because it is the part with a rule in it and the hook is the part
 * with a context in it, and this repo has no DOM tests — a decision reachable
 * only through a rendered component is a decision nothing asserts. The
 * optimistic-while-unknown branch in particular is the one that would rot
 * silently: it looks like a missing null check.
 */
export function holds(held: Set<Capability> | undefined, capabilities: Capability[]): boolean {
  if (!held) return true;
  return capabilities.every((c) => held.has(c));
}

/**
 * May this session do ANY of these things?
 *
 * For a place rather than an action (org-admin#131): the Notifications screen
 * is worth showing to a role holding either of its two read permissions, and
 * absent only for a role holding neither. Optimistic while unknown, like
 * `useCan`, so a failed capabilities fetch shows the entry and lets the server
 * refuse, rather than hiding a screen somebody is allowed to open.
 */
/**
 * The held set itself, for a caller deciding over several gates at once: the
 * nav hides a group heading when none of its entries survive. `undefined`
 * while unknown, so callers go through `holds`/`holdsAny` rather than
 * reading it directly and losing the optimistic branch.
 */
export function useCapabilitiesLoading(): boolean {
  return useContext(CapabilitiesLoading);
}

export function useHeldCapabilities(): Set<Capability> | undefined {
  return useContext(CapabilityContext);
}

export function useCanAny(...capabilities: Capability[]): boolean {
  return holdsAny(useContext(CapabilityContext), capabilities);
}

export function holdsAny(held: Set<Capability> | undefined, capabilities: Capability[]): boolean {
  if (!held || capabilities.length === 0) return true;
  return capabilities.some((c) => held.has(c));
}

/**
 * Where "home" is for this session: the first nav entry it may open.
 *
 * Overview is first for any role it has something to show, and Legal Admin's
 * Overview was a single Agreements figure on an empty page (Jon, 2026-09-16,
 * auth#518). So Overview is gated like every other entry, and a session that
 * cannot open it lands on its first screen instead. Decided from the nav's own
 * order and gates, so no role is named here. `null` when nothing is allowed.
 */
export function firstAllowedPath(
  held: Set<Capability> | undefined,
  entries: readonly { path: string; anyOf?: readonly Capability[] }[],
): string | null {
  return entries.find((e) => holdsAny(held, [...(e.anyOf ?? [])]))?.path ?? null;
}

/**
 * The nav's entries in the order home is looked for: `first` (by path, in that
 * order), then the rest in nav order.
 *
 * Needed once Legal moved below Subscriptions (Jon, 2026-10-15). In nav order
 * a Legal Admin, whose `optOut:read` opens Notifications, would land on the
 * opt-out list rather than on the documents they administer. Home is about
 * which screen a role is FOR; the sidebar is about where things sit.
 */
export function homeOrder<E extends { path: string }>(entries: readonly E[], first: readonly string[]): E[] {
  const preferred = first.flatMap((p) => entries.filter((e) => e.path === p));
  return [...preferred, ...entries.filter((e) => !first.includes(e.path))];
}

/**
 * What a gated SCREEN does, given the capability answer (org-admin#134).
 *
 * Unlike a button, a screen waits while the answer is loading, because screens
 * act on mount and CI fleet's action is a sign-in redirect. A failed fetch is
 * not loading, so it falls through to `holdsAny`'s optimistic yes. An ungated
 * screen never waits.
 */
export function screenGate(
  loading: boolean,
  held: Set<Capability> | undefined,
  anyOf: readonly Capability[] | undefined,
): 'wait' | 'show' | 'refuse' {
  if (!anyOf || anyOf.length === 0) return 'show';
  if (loading) return 'wait';
  return holdsAny(held, [...anyOf]) ? 'show' : 'refuse';
}

/**
 * The sentence a disabled control explains itself with.
 *
 * ── A DISABLED CONTROL WITH NO EXPLANATION READS AS A BUG ────────────
 *
 * The same rule the Access tab's disabled Remove already follows. A faded button
 * with no tooltip is indistinguishable from a broken feature, and the person
 * looking at it cannot tell whether to ask for access or file a defect.
 *
 * Says what is missing, not which role would have it. "You need Legal Admin"
 * would be this repo asserting a role-to-capability mapping again — the exact
 * copy this module exists to avoid — and it would be wrong the moment a second
 * role gains the capability, which is precisely what happened to `agreement:read`
 * on 2026-08-28.
 */
export function whyDisabled(action: string): string {
  return `Your role does not allow ${action}.`;
}
